interface QueueItem<T> {
  id: string;
  priority: number;
  data: T;
}

export class PriorityQueue<T> {
  private queue: string[] = [];
  private map: Record<string, QueueItem<T>> = {};
  private batchSize: number;

  constructor({ batchSize = 3 }: { batchSize?: number } = {}) {
    this.batchSize = batchSize;
  }

  enqueue(item: QueueItem<T>) {
    this.map[item.id] = item;

    if (!item.priority) {
      item.priority = 0;
      this.queue.push(item.id);
      return;
    }

    const index = this.queue.findIndex(
      (id) => this.map[id].priority < item.priority,
    );
    if (index === -1) {
      this.queue.push(item.id);
    } else {
      this.queue.splice(index, 0, item.id);
    }
  }

  update(id: string, updates: Partial<QueueItem<T>>) {
    if (!this.map[id]) return;
    this.map[id] = { ...this.map[id], ...updates };
  }

  drop(id: string) {
    const index = this.queue.indexOf(id);
    if (index === -1) return;
    this.queue.splice(index, 1);
    delete this.map[id];
  }

  dequeue(): T | undefined {
    const [id, ...rest] = this.queue;
    this.queue = rest;
    const item = this.map[id];
    delete this.map[id];
    return item?.data;
  }

  getNextBatch(): QueueItem<T>[] {
    const ids = this.queue.splice(0, this.batchSize);
    const items = ids.reduce((acc, id) => {
      const item = this.map[id];
      if (!item) return acc;

      acc.push(item);
      delete this.map[id];
      return acc;
    }, [] as QueueItem<T>[]);
    return items;
  }

  clear() {
    this.queue = [];
    this.map = {};
  }
}
